import type { Task, TaskStatus } from "./warehouse-types";
import { warehousePreparationOwnerRole } from "../warehouse-preparation-view";

export const labels: Record<string, string> = {
  AUTO_CREATED: "Önceki rota adımı bekleniyor",
  WAREHOUSE_PENDING: "Depo işlemi bekliyor",
  PICKING: "Toplanıyor",
  PARTIALLY_PREPARED: "Kısmi hazırlandı",
  READY_FOR_TRANSFER: "Transfer için hazır",
  DISPATCHED: "Cep depoya sevk edildi",
  STAGING_CONFIRMATION_PENDING: "Teslim teyidi bekliyor",
  PHYSICAL_CONFIRMED: "Teslim teyidi tamamlandı",
  READY: "Üretime hazır",
  STOCK_SHORTAGE: "Eksik stok",
  SYSTEM_PHYSICAL_VARIANCE: "Fiziki-sistemsel fark",
  WRONG_LOCATION: "Yanlış lokasyon / malzeme",
  INVALID_MATERIAL: "Reçete veya birim verisi hatalı",
  BLOCKED: "Engellendi",
  UNRESOLVED: "Çözülemedi",
  ESCALATION_REQUIRED: "Eskalasyon gerekli",
  EXCEPTION_CLOSED: "İstisna ile kapatıldı",
};
export const exceptionStatuses = new Set<TaskStatus>([
  "STOCK_SHORTAGE",
  "SYSTEM_PHYSICAL_VARIANCE",
  "WRONG_LOCATION",
  "BLOCKED",
  "UNRESOLVED",
  "ESCALATION_REQUIRED",
]);
export const fmt = (value: number, unit: string) =>
  `${new Intl.NumberFormat("tr-TR", { maximumFractionDigits: 3 }).format(value)} ${unit}`;

export function routeLabel(task: Task) {
  if (task.routeLeg === "VERIFY_STAGING")
    return `Cep depo ${task.stagingWarehouseCode}`;
  if (task.sourceWarehouseCode && task.targetWarehouseCode)
    return `${task.sourceWarehouseCode} → ${task.targetWarehouseCode}`;
  return task.targetWarehouseCode ?? task.sourceWarehouseCode ?? "Depo rotası";
}

export function warehouseRoleCopy(role: string) {
  if (role === "CEP_DEPO_SORUMLUSU")
    return {
      title: "Cep depo teslim ve sayım",
      description: "Gelen malzemeyi kontrol edin, fiziki miktarı sayın ve teslimi teyit edin.",
      steps: ["Malzeme kodunu kontrol et", "Fiziki miktarı say", "Teslimi teyit et"],
      emptyTitle: "Teyit bekleyen malzeme yok",
      emptyDescription: "Depodan sevk edilen malzemeler burada görünecek.",
    };
  if (role === "DEPO_GOREVLISI")
    return {
      title: "Depodan malzeme çıkışı",
      description: "Sıradaki görevi alın, miktarı hazırlayın ve cep depoya sevk edin.",
      steps: ["Görevi üzerine al", "Malzemeyi hazırla", "Sevki tamamla"],
      emptyTitle: "Hazırlanacak malzeme yok",
      emptyDescription: "T-1 kontrolünden ihtiyaç çıktığında görevler burada görünecek.",
    };
  return {
    title: "Depo hazırlık kontrol merkezi",
    description: "Depo ve cep depo görevlerini, gecikmeleri ve teyitleri izleyin.",
    steps: [],
    emptyTitle: "Açık hazırlık görevi yok",
    emptyDescription: "T-1 kontrolünden aksiyon çıktığında görevler burada oluşur.",
  };
}

export function warehouseGroupTitle(key: string, role: string, fallback: string) {
  if (role === "CEP_DEPO_SORUMLUSU") {
    if (key === "confirmation") return "Şimdi sayılacak ve teyit edilecek";
    if (key === "work") return "Bugün teslim alınacak";
    if (key === "upcoming") return "Daha sonra gelecek";
    if (key === "ready") return "Tamamlanan teyitler";
  }
  if (role === "DEPO_GOREVLISI") {
    if (key === "work") return "Şimdi hazırlanacak";
    if (key === "upcoming") return "Sıradaki işler";
    if (key === "confirmation") return "Cep depo teyidi bekleyen sevkler";
    if (key === "ready") return "Tamamlanan sevkler";
  }
  return fallback;
}

export function taskInstruction(task: Task) {
  if (task.status === "AUTO_CREATED")
    return "Önceki depo adımı tamamlandığında bu görev otomatik açılır.";
  if (task.status === "WAREHOUSE_PENDING")
    return "Görevi alın ve malzemeyi toplamaya başlayın.";
  if (task.status === "PICKING" || task.status === "PARTIALLY_PREPARED")
    return "Hazırladığınız miktarı girin; tamamlandıysa sevke çıkarın.";
  if (task.status === "READY_FOR_TRANSFER")
    return "Malzemeyi hedef depoya sevk ettiğinizde işlemi tamamlayın.";
  if (task.status === "DISPATCHED")
    return "Sevk cep depoya ulaştığında teslim adımını başlatın.";
  if (task.status === "STAGING_CONFIRMATION_PENDING")
    return "Gelen malzemenin kodunu kontrol edin; teslim sonrası cep depodaki toplam miktarı sayıp teyit edin.";
  if (exceptionStatuses.has(task.status))
    return "Sorunu kontrol edin; işleme devam edin veya eskalasyon başlatın.";
  return "Bu görev için açık işlem bulunmuyor.";
}

export function duration(value: number | null) {
  if (value === null) return "—";
  if (value < 60) return `${Math.round(value)} dk`;
  return `${new Intl.NumberFormat("tr-TR", { maximumFractionDigits: 1 }).format(value / 60)} sa`;
}

export function nextActions(
  task: Task,
  role: string,
): Array<{ status: TaskStatus; label: string }> {
  const ownerRole = warehousePreparationOwnerRole(task);
  const stagingOwned = ownerRole === "CEP_DEPO_SORUMLUSU";
  const planningOwned = ownerRole === "PLANLAMA";
  const canOperate = role === "SISTEM_YONETICISI" || role === ownerRole;
  if (!canOperate) return [];
  if (planningOwned && task.status === "UNRESOLVED")
    return [{ status: "ESCALATION_REQUIRED", label: "Eskalasyon iste" }];
  if (planningOwned && task.status === "ESCALATION_REQUIRED")
    return [{ status: "UNRESOLVED", label: "Çözülemedi olarak kaydet" }];
  if (task.status === "STOCK_SHORTAGE")
    return [
      ...(planningOwned
        ? []
        : ([{ status: "PICKING", label: "Toplamaya geri dön" }] as Array<{
            status: TaskStatus;
            label: string;
          }>)),
      { status: "UNRESOLVED", label: "Çözülemedi" },
      { status: "ESCALATION_REQUIRED", label: "Eskalasyon iste" },
    ];
  if (task.status === "INVALID_MATERIAL")
    return [
      { status: "UNRESOLVED", label: "Çözülemedi" },
      { status: "ESCALATION_REQUIRED", label: "Eskalasyon iste" },
    ];
  if (task.status === "SYSTEM_PHYSICAL_VARIANCE")
    return [
      {
        status: stagingOwned ? "STAGING_CONFIRMATION_PENDING" : "PICKING",
        label: "Yeniden say / araştır",
      },
      { status: "UNRESOLVED", label: "Çözülemedi" },
      { status: "ESCALATION_REQUIRED", label: "Eskalasyon iste" },
    ];
  if (task.status === "WRONG_LOCATION")
    return [
      {
        status: stagingOwned ? "STAGING_CONFIRMATION_PENDING" : "PICKING",
        label: "Kontrole devam et",
      },
      { status: "UNRESOLVED", label: "Çözülemedi" },
    ];
  if (
    task.status === "BLOCKED" ||
    task.status === "UNRESOLVED" ||
    task.status === "ESCALATION_REQUIRED"
  )
    return [
      {
        status: stagingOwned
          ? "STAGING_CONFIRMATION_PENDING"
          : planningOwned
            ? "UNRESOLVED"
            : "WAREHOUSE_PENDING",
        label: "İşleme geri al",
      },
      ...(task.status !== "ESCALATION_REQUIRED"
        ? ([
            { status: "ESCALATION_REQUIRED", label: "Eskalasyon iste" },
          ] as Array<{ status: TaskStatus; label: string }>)
        : []),
    ];
  if (task.routeLeg === "OPEN_TRANSFER_MONITOR") {
    if (task.status === "WAREHOUSE_PENDING")
      return [{ status: "PICKING", label: "Transferi izlemeye al" }];
    if (task.status === "PICKING")
      return [
        { status: "BLOCKED", label: "Transfer gecikti" },
        { status: "UNRESOLVED", label: "Çözülemedi" },
      ];
    return [];
  }
  if (stagingOwned) {
    if (task.status === "DISPATCHED")
      return [{ status: "STAGING_CONFIRMATION_PENDING", label: "Teslim al" }];
    if (task.status === "STAGING_CONFIRMATION_PENDING")
      return [
        { status: "PHYSICAL_CONFIRMED", label: "Teslimi teyit et" },
        { status: "SYSTEM_PHYSICAL_VARIANCE", label: "Miktar farklı" },
        { status: "WRONG_LOCATION", label: "Yanlış malzeme" },
        { status: "BLOCKED", label: "Teslim edilemedi" },
      ];
    return [];
  }
  if (task.status === "WAREHOUSE_PENDING")
    return [{ status: "PICKING", label: "Görevi al ve başla" }];
  if (task.status === "PICKING")
    return [
      { status: "PARTIALLY_PREPARED", label: "Kısmi kaydet" },
      { status: "READY_FOR_TRANSFER", label: "Transfer için hazır" },
      { status: "STOCK_SHORTAGE", label: "Stok yetersiz" },
      { status: "WRONG_LOCATION", label: "Yanlış lokasyon" },
      { status: "SYSTEM_PHYSICAL_VARIANCE", label: "Stok farkı" },
      { status: "BLOCKED", label: "Engellendi" },
    ];
  if (task.status === "PARTIALLY_PREPARED")
    return [
      { status: "PICKING", label: "Toplamaya devam" },
      { status: "READY_FOR_TRANSFER", label: "Transfer için hazır" },
    ];
  if (task.status === "READY_FOR_TRANSFER")
    return [{ status: "DISPATCHED", label: "Sevk edildi" }];
  return [];
}
